import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Skeleton,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import {
  Inventory,
  Category,
  WorkspacePremium,
  Handshake,
  ArrowForward,
  Add,
  TrendingUp,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

// Styled components
const StatCard = styled(Card)<{ $gradient?: string }>`
  height: 100%;
  border-radius: 16px;
  border: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => props.$gradient || "linear-gradient(90deg, #d32f2f, #f44336)"};
  }
`;

const QuickActionCard = styled(Card)`
  height: 100%;
  border-radius: 16px;
  border: 1px solid #eee;
  box-shadow: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #d32f2f;
    box-shadow: 0 8px 24px rgba(211, 47, 47, 0.1);
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled(Box)<{ $color?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${(props) => props.$color || "rgba(211, 47, 47, 0.1)"};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

type Stats = {
  products: number;
  categories: number;
  certificates: number;
  references: number;
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError("");
    try {
      // Gerçek JSON dosyalarından istatistikleri çek
      const [productsRes, categoriesRes, certificatesRes, referencesRes] =
        await Promise.all([
          fetch("/data/products/index.json", { cache: "no-store" }),
          fetch("/data/categories/index.json", { cache: "no-store" }),
          fetch("/data/certificates/index.json", { cache: "no-store" }),
          fetch("/data/company/references.json", { cache: "no-store" }),
        ]);

      const products = await productsRes.json().catch(() => ({ items: [] }));
      const categories = await categoriesRes.json().catch(() => ({ items: [] }));
      const certificates = await certificatesRes.json().catch(() => ({ items: [] }));
      const references = await referencesRes.json().catch(() => ({ items: [] }));

      setStats({
        products: Array.isArray(products.items) ? products.items.length : 0,
        categories: Array.isArray(categories.items) ? categories.items.length : 0,
        certificates: Array.isArray(certificates.items) ? certificates.items.length : 0,
        references: Array.isArray(references.items) ? references.items.length : 0,
      });
    } catch (e: any) {
      setError("İstatistikler yüklenemedi");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Toplam Ürün",
      value: stats?.products ?? 0,
      icon: <Inventory sx={{ fontSize: 28, color: "#2196f3" }} />,
      color: "rgba(33, 150, 243, 0.1)",
      gradient: "linear-gradient(90deg, #2196f3, #64b5f6)",
      path: "/admin/products",
    },
    {
      title: "Kategoriler",
      value: stats?.categories ?? 0,
      icon: <Category sx={{ fontSize: 28, color: "#4caf50" }} />,
      color: "rgba(76, 175, 80, 0.1)",
      gradient: "linear-gradient(90deg, #4caf50, #81c784)",
      path: "/admin/categories",
    },
    {
      title: "Sertifikalar",
      value: stats?.certificates ?? 0,
      icon: <WorkspacePremium sx={{ fontSize: 28, color: "#ff9800" }} />,
      color: "rgba(255, 152, 0, 0.1)",
      gradient: "linear-gradient(90deg, #ff9800, #ffb74d)",
      path: "/admin/certificates",
    },
    {
      title: "Referanslar",
      value: stats?.references ?? 0,
      icon: <Handshake sx={{ fontSize: 28, color: "#9c27b0" }} />,
      color: "rgba(156, 39, 176, 0.1)",
      gradient: "linear-gradient(90deg, #9c27b0, #ba68c8)",
      path: "/admin/references",
    },
  ];

  const quickActions = [
    {
      title: "Yeni Ürün Ekle",
      description: "Kataloga yeni bir kapı modeli ekleyin",
      icon: <Add sx={{ fontSize: 28, color: "#d32f2f" }} />,
      color: "rgba(211, 47, 47, 0.1)",
      action: () => navigate("/admin/products"),
      primary: true,
    },
    {
      title: "Ürünleri Yönet",
      description: "Mevcut ürünleri düzenleyin, silin veya kopyalayın",
      icon: <Inventory sx={{ fontSize: 28, color: "#1976d2" }} />,
      color: "rgba(25, 118, 210, 0.1)",
      action: () => navigate("/admin/products"),
    },
    {
      title: "Kategoriler",
      description: "Ürün kategorilerini düzenleyin",
      icon: <Category sx={{ fontSize: 28, color: "#388e3c" }} />,
      color: "rgba(56, 142, 60, 0.1)",
      action: () => navigate("/admin/categories"),
    },
    {
      title: "Sertifikalar",
      description: "Yangın sertifikalarını yönetin",
      icon: <WorkspacePremium sx={{ fontSize: 28, color: "#f57c00" }} />,
      color: "rgba(245, 124, 0, 0.1)",
      action: () => navigate("/admin/certificates"),
    },
    {
      title: "Firma Bilgileri",
      description: "İletişim ve sosyal medya bilgilerini güncelleyin",
      icon: <TrendingUp sx={{ fontSize: 28, color: "#7b1fa2" }} />,
      color: "rgba(123, 31, 162, 0.1)",
      action: () => navigate("/admin/company"),
    },
    {
      title: "Referanslar",
      description: "Ana sayfa referans kartlarını düzenleyin",
      icon: <Handshake sx={{ fontSize: 28, color: "#00796b" }} />,
      color: "rgba(0, 121, 107, 0.1)",
      action: () => navigate("/admin/references"),
    },
  ];

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a1a1a", mb: 1 }}>
          Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Yanmaz Kapı yönetim paneline hoş geldiniz
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <StatCard $gradient={stat.gradient}>
              <CardContent sx={{ p: 3 }}>
                {loading ? (
                  <>
                    <Skeleton variant="circular" width={56} height={56} sx={{ mb: 2 }} />
                    <Skeleton width="60%" height={32} />
                    <Skeleton width="40%" height={24} />
                  </>
                ) : (
                  <>
                    <IconWrapper $color={stat.color}>{stat.icon}</IconWrapper>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, color: "#1a1a1a", mb: 0.5 }}
                    >
                      {stat.value}
                    </Typography>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {stat.title}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                        onClick={() => navigate(stat.path)}
                        sx={{ fontSize: "0.75rem", minWidth: 0 }}
                      >
                        Görüntüle
                      </Button>
                    </Stack>
                  </>
                )}
              </CardContent>
            </StatCard>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          Hızlı İşlemler
        </Typography>
        <Grid container spacing={3}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <QuickActionCard
                onClick={action.action}
                sx={
                  action.primary
                    ? {
                        background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                        border: "none",
                        "& *": { color: "white !important" },
                        "&:hover": {
                          background: "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)",
                        },
                      }
                    : {}
                }
              >
                <CardContent sx={{ p: 3 }}>
                  <IconWrapper
                    $color={action.primary ? "rgba(255,255,255,0.2)" : action.color}
                  >
                    {action.icon}
                  </IconWrapper>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {action.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: action.primary ? "rgba(255,255,255,0.8)" : "text.secondary" }}
                  >
                    {action.description}
                  </Typography>
                </CardContent>
              </QuickActionCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* System Status */}
      <Card sx={{ borderRadius: 3, border: "1px solid #eee", boxShadow: "none" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Sistem Durumu
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Chip
              label="API Bağlantısı: Aktif"
              color="success"
              size="small"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              label="GitHub Entegrasyonu: Aktif"
              color="success"
              size="small"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              label={`Son güncelleme: ${new Date().toLocaleDateString("tr-TR")}`}
              variant="outlined"
              size="small"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminDashboard;
